import { FC, useEffect, useRef } from 'react'
import { ArrowRight, Zap } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import { useNavigate } from 'react-router-dom'

export const CTASection: FC = () => {
  const navigate = useNavigate()
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view')
          }
        })
      },
      { threshold: 0.2 }
    )

    if (sectionRef.current) observer.observe(sectionRef.current)
    return () => observer.disconnect()
  }, [])

  return (
    <section className="relative px-6 md:px-12 lg:px-24 py-24 md:py-32 overflow-hidden">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-sumoPrimary/10 via-sumoAmber/5 to-sumoOrange/10 dark:from-sumoPrimary/5 dark:via-sumoAmber/3 dark:to-sumoOrange/5" />
      
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-sumoPrimary/20 dark:bg-sumoPrimary/10 rounded-full blur-3xl pointer-events-none" />

      <div 
        ref={sectionRef}
        className="animate-on-scroll relative max-w-3xl mx-auto text-center"
      >
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 dark:bg-neutral800/80 border border-sumoPrimary/20 backdrop-blur-sm mb-8">
          <Zap className="w-4 h-4 text-sumoPrimary" />
          <span className="text-sm font-medium text-neutral700 dark:text-neutral300">
            Start in under 2 minutes
          </span>
        </div>

        {/* Headline - Peak moment, memorable */}
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-neutral900 dark:text-neutral100 tracking-tight mb-6">
          Ready to simplify
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-sumoPrimary via-sumoAmber to-sumoOrange bg-gradient-animate">
            your workflow?
          </span>
        </h2>

        {/* Subheadline - Positive resolution */}
        <p className="text-lg md:text-xl text-neutral600 dark:text-neutral400 mb-10 max-w-xl mx-auto leading-relaxed">
          Join thousands of sellers who've already made the switch. Your inventory chaos ends here.
        </p>

        {/* Single primary CTA - Clear end action */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate('/create-account')}
            className="group btn-hover-lift btn-glow text-base px-10 py-6 rounded-xl shadow-yellow hover:shadow-strong"
          >
            Get Started Free
            <ArrowRight 
              className="ml-2 transition-transform duration-200 group-hover:translate-x-1" 
              width={20} 
              height={20} 
            />
          </Button>
        </div>

        {/* Trust signal */}
        <p className="mt-6 text-sm text-neutral500 dark:text-neutral400">
          No credit card required
        </p>
      </div>
    </section>
  )
}
